import { makeAutoObservable } from 'mobx'
import { type AnalyticsChartPeriod } from 'entities/Analytics'
import {
  type AnalyticsResponse,
  type IPerformanceChartResponse,
  type IPerformanceStatisticResponse,
  type PerformanceRechartsDataPoint,
  type ProcessedStatistic,
} from 'entities/Analytics/api/types'
import { formatDateForDisplay, calculateTrend, computeHasStatistic } from 'pages/analytics/lib'

export class PerformanceChartDataStore {
  private _rawStatistic: IPerformanceStatisticResponse[] = []
  private _rawChartData: IPerformanceChartResponse[] = []
  private _period: AnalyticsChartPeriod | null = null

  constructor() {
    makeAutoObservable(this)
  }

  setRawData = (
    response: AnalyticsResponse<IPerformanceStatisticResponse, IPerformanceChartResponse>,
    period?: AnalyticsChartPeriod
  ) => {
    this._rawStatistic = response.statistic
    this._rawChartData = response.charts
    if (period) {
      this._period = period
    }
  }

  get processedStatistic(): ProcessedStatistic[] {
    return this._rawStatistic.map((item) => {
      const { key, value, prevValue, label } = item
      const trend = calculateTrend(value, prevValue)

      return {
        key: key,
        label: label || key,
        value: value,
        prevValue: prevValue,
        trend,
      }
    })
  }

  get rechartsData(): PerformanceRechartsDataPoint[] {
    return this._rawChartData
      .map((item) => ({
        date: item.date,
        name: formatDateForDisplay(item.date, this._period ?? 'week'),
        avg_time_to_first_response: item.avg_time_to_first_response || 0,
        avg_time_to_response: item.avg_time_to_response || 0,
        avg_time_to_close: item.avg_time_to_close || 0,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }

  getStatisticByKey = (key: string): ProcessedStatistic | undefined => {
    return this.processedStatistic.find((item) => item.key === key)
  }

  get hasStatistic() {
    return computeHasStatistic(this._rawStatistic)
  }

  dispose = () => {
    this._rawStatistic = []
    this._rawChartData = []
    this._period = null
  }
}
